/**
 * "Unavailable" bucket helpers shared by the SOH and RUL bucket hooks.
 *
 * The backend returns every in-scope battery. A battery with no SOH (or no RUL for
 * a unit) is grouped under a fixed wire label: "SOH Unavailable" / "RUL Unavailable".
 * That wire label is also the only value the backend accepts in a bucket filter, so:
 *   - bucket.name         is always the wire label (send this to the API)
 *   - bucket.displayLabel is what the UI shows (org meta label, else the wire label)
 *
 * Org meta may describe the bucket with an entry marked `"includeNull": true`, e.g.
 *   { "label": "SOH Unavailable", "color": "#8A94A6", "range": { "max": 0 }, "includeNull": true }
 * That entry must be removed before range parsing: the range parsers default a
 * missing min to 0, which would turn it into a catch-all range bucket.
 */

export const SOH_UNAVAILABLE_LABEL = "SOH Unavailable";
export const RUL_UNAVAILABLE_LABEL = "RUL Unavailable";

// Slate grey: reads as "no data" next to the green-to-red / blue palettes.
export const UNAVAILABLE_BUCKET_COLOR = "#8A94A6";

const UNAVAILABLE_WIRE_LABELS = new Set([SOH_UNAVAILABLE_LABEL, RUL_UNAVAILABLE_LABEL]);

/**
 * Python bool() truthiness, so the frontend reads `includeNull` exactly as the backend
 * does (bool(bucket.get("includeNull"))): falsy are None/False/0/""/[]/{}; everything
 * else is truthy, including the string "false".
 */
export function isPythonTruthy(value) {
  if (value == null || value === false) return false;
  if (typeof value === "number") return value !== 0; // NaN is truthy in Python too
  if (typeof value === "string") return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return Boolean(value);
}

/** Python isinstance(value, dict) for JSON values: a plain object, not an array or null. */
function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/** A non-empty string (Python isinstance(v, str) and v). */
function isNonEmptyString(value) {
  return typeof value === "string" && value.length > 0;
}

/**
 * True for an org meta entry that describes the unavailable bucket.
 * A non-object entry (null, string, array, number) is never one, as in the backend.
 */
export function isUnavailableEntry(entry, wireLabel) {
  if (!isPlainObject(entry)) return false;
  return isPythonTruthy(entry.includeNull) || (wireLabel != null && entry.label === wireLabel);
}

/**
 * Bound rule shared with the backend (_classifyOrgMetaBucketEntry): a bound is a finite
 * number (Number.isFinite does not coerce, so bool and numeric strings fail), or missing / null
 * for open-ended. Valid only when no present bound is invalid AND at least one is finite.
 */
function isFiniteBound(value) {
  return Number.isFinite(value);
}

function hasUsableBounds(min, max) {
  if (min != null && !isFiniteBound(min)) return false;
  if (max != null && !isFiniteBound(max)) return false;
  return isFiniteBound(min) || isFiniteBound(max);
}

/**
 * Exactly the backend rule for an org meta entry that becomes a range branch of the
 * bucket CASE: a plain-object entry with a non-empty string label, not an unavailable
 * entry, with a plain-object `range` whose bounds pass hasUsableBounds. Flat
 * { label, min, max } entries are not range entries for the backend.
 */
export function isServerRangeEntry(entry, wireLabel) {
  if (!isPlainObject(entry)) return false;
  if (!isNonEmptyString(entry.label)) return false;
  if (isUnavailableEntry(entry, wireLabel)) return false;
  if (!isPlainObject(entry.range)) return false;
  return hasUsableBounds(entry.range.min, entry.range.max);
}

/**
 * Where an entry's bounds live: the nested `range` when the entry has that key at all
 * (so `"range": null` is judged, and rejected, as the backend does), else the flat
 * entry itself. Parsing reads min from the same source it validated.
 */
function getBoundsSource(entry) {
  return entry.range !== undefined ? entry.range : entry;
}

/**
 * True for an entry the frontend may turn into a range bucket. A nested entry must pass
 * the backend rule exactly (isServerRangeEntry), so a trusted unit's legend holds the
 * same entries the backend builds. A flat { label, min, max } entry (no `range` key,
 * merged pageMeta) is held to the same label and bound rules on its own min / max.
 */
export function isUsableRangeEntry(entry, wireLabel) {
  if (!isPlainObject(entry)) return false;
  if (entry.range !== undefined) return isServerRangeEntry(entry, wireLabel);
  if (!isNonEmptyString(entry.label)) return false;
  if (isUnavailableEntry(entry, wireLabel)) return false;
  return hasUsableBounds(entry.min, entry.max);
}

/**
 * Splits a raw org meta bucket list into the usable range entries and the (optional)
 * unavailable entry. Call this BEFORE range parsing. A null / non-array list is empty;
 * malformed entries (see isUsableRangeEntry) are dropped here, so the parser never
 * sees them.
 * @returns {{ rangeEntries: Array, unavailableEntry: object|null }}
 */
export function splitUnavailableEntry(entries, wireLabel) {
  const list = Array.isArray(entries) ? entries : [];
  const unavailableEntry = list.find((e) => isUnavailableEntry(e, wireLabel)) ?? null;
  const rangeEntries = list.filter((e) => isUsableRangeEntry(e, wireLabel));
  return { rangeEntries, unavailableEntry };
}

/**
 * Sorts usable range entries best -> worst (highest min first) and maps them to
 * { name, displayLabel, color, min }. Uses `fallback` when there are none. A missing /
 * null min (open lower bound) is 0. Expects the output of splitUnavailableEntry.
 */
export function parseRangeBuckets(rangeEntries, fallback) {
  const source = rangeEntries?.length > 0 ? rangeEntries : fallback;
  const minOf = (entry) => getBoundsSource(entry).min ?? 0;
  return [...source]
    .sort((a, b) => minOf(b) - minOf(a))
    .map((entry) => ({
      name:         entry.label,
      displayLabel: entry.label,
      color:        isNonEmptyString(entry.color) ? entry.color : "#CCCCCC",
      min:          minOf(entry),
    }));
}

/**
 * Builds the unavailable bucket. `name` is always the wire label. The org entry's label
 * and colour are used only when they are non-empty strings (an object label would crash
 * React when rendered).
 */
export function buildUnavailableBucket(wireLabel, unavailableEntry = null) {
  return {
    name:          wireLabel,
    displayLabel:  isNonEmptyString(unavailableEntry?.label) ? unavailableEntry.label : wireLabel,
    color:         isNonEmptyString(unavailableEntry?.color) ? unavailableEntry.color : UNAVAILABLE_BUCKET_COLOR,
    min:           null,
    isUnavailable: true,
  };
}

/**
 * Returns the parsed range buckets with one unavailable bucket appended LAST.
 * Any stray unavailable bucket already in the list (by flag or wire label) is dropped
 * first so it can never appear twice or act as a range.
 */
export function appendUnavailableBucket(parsedBuckets, wireLabel, unavailableEntry = null) {
  const ranges = (parsedBuckets || []).filter(
    (b) => !b?.isUnavailable && b?.name !== wireLabel
  );
  return [...ranges, buildUnavailableBucket(wireLabel, unavailableEntry)];
}

/** True for the unavailable bucket object, or for either wire label string. */
export function isUnavailableBucket(nameOrBucket) {
  if (nameOrBucket && typeof nameOrBucket === "object") {
    return nameOrBucket.isUnavailable === true || UNAVAILABLE_WIRE_LABELS.has(nameOrBucket.name);
  }
  return UNAVAILABLE_WIRE_LABELS.has(nameOrBucket);
}

/** A metric value that cannot be classified into a range: null, empty, NaN or negative. */
export function isUnavailableValue(value) {
  if (value == null || value === "") return true;
  const n = Number(value);
  return !Number.isFinite(n) || n < 0;
}

/**
 * Classifies a value into a bucket name.
 *   - null / NaN / negative -> the unavailable bucket's name (if the list has one), else null
 *   - otherwise the first range bucket (sorted best -> worst) whose min the value reaches.
 * The unavailable bucket never takes part in range matching.
 * @returns {string|null}
 */
export function classifyBucketValue(value, buckets) {
  if (!buckets?.length) return null;
  if (isUnavailableValue(value)) {
    return buckets.find((b) => b.isUnavailable)?.name ?? null;
  }
  const n = Number(value);
  for (const bucket of buckets) {
    if (bucket.isUnavailable || bucket.min == null) continue;
    if (n >= bucket.min) return bucket.name;
  }
  return null;
}

/** True when `row` (a response metaInfo object) carries the `key` field at all. */
export function hasServerBucket(row, key) {
  return row != null && typeof row === "object" && key != null
    && Object.prototype.hasOwnProperty.call(row, key);
}

/**
 * Resolves a row's bucket name from the server bucket field `row[key]`.
 *   - field absent from the row      -> client-side classification of the raw value
 *   - field present with a label     -> that label (server boundaries are authoritative)
 *   - field present but null / ""    -> the server left the value unclassified: it sits in
 *     a gap between org meta ranges or above the top max. The donut has no slice for it,
 *     so it gets no range bucket (null = not plotted). A null / NaN / negative value
 *     still resolves to the unavailable bucket.
 * Callers that must not trust the server field for a unit pass row = null.
 * @returns {string|null}
 */
export function resolveBucketName(row, key, value, buckets) {
  if (!hasServerBucket(row, key)) return classifyBucketValue(value, buckets);
  const label = row[key];
  if (label != null && label !== "") return label;
  if (isUnavailableValue(value)) return (buckets || []).find((b) => b.isUnavailable)?.name ?? null;
  return null;
}

/** UI label for a bucket name (wire label -> org display label). */
export function getBucketDisplayLabel(name, buckets) {
  const bucket = (buckets || []).find((b) => b.name === name);
  return bucket?.displayLabel ?? name;
}
